import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Search } from 'lucide-react';
import { AdminUser, ActivityEntry, useAdminActivity, useAdminUsers, useResetAdminPassword, useSetAdminActive } from '../../queries/adminUsers';
import { InviteAdminDialog } from './InviteAdminDialog';
import { RoleAccessGuide } from './RoleAccessGuide';
import { useToast } from '../../contexts/ToastContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

const DATETIME = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
const fmt = (v: string | null) => (v ? DATETIME.format(new Date(v)) : '—');
const name = (u: AdminUser) => [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';

export function AdminUsersPage() {
  const { data: users = [], isLoading } = useAdminUsers();
  const [page, setPage] = useState(1);
  const [adminFilter, setAdminFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  // Any filter change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [adminFilter, debouncedSearch]);
  const { data: activity, isLoading: activityLoading } = useAdminActivity(page, {
    adminUserId: adminFilter ? Number(adminFilter) : null,
    search: debouncedSearch || undefined,
  });
  const pagination = activity?.pagination;

  const [showInvite, setShowInvite] = useState(false);
  const [reset, setReset] = useState<{ email: string; password: string } | null>(null);
  const resetPassword = useResetAdminPassword();
  const setActive = useSetAdminActive();
  const { success, error } = useToast();

  const handleReset = (u: AdminUser) =>
    resetPassword.mutate(u.id, {
      onSuccess: (res) => setReset({ email: u.email, password: res.temporaryPassword }),
      onError: () => error(`Couldn't reset the password for ${u.email}.`),
    });

  const handleSetActive = (u: AdminUser) =>
    setActive.mutate(
      { id: u.id, is_active: !u.is_active },
      {
        onSuccess: () => success(`${u.email} ${u.is_active ? 'deactivated' : 'reactivated'}.`),
        onError: () => error(`Couldn't update ${u.email}.`),
      },
    );

  const staffColumns: DataTableColumn<AdminUser>[] = [
    { key: 'email', header: 'Email', priority: true, render: (u) => <span className="font-medium">{u.email}</span> },
    { key: 'name', header: 'Name', priority: false, render: (u) => <span className="text-muted-foreground @2xl:text-foreground">{name(u)}</span> },
    { key: 'role', header: 'Role', priority: true, render: (u) => <Badge variant="default">{u.role}</Badge> },
    { key: 'status', header: 'Status', priority: false, render: (u) => (u.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="outline">Inactive</Badge>) },
    { key: 'login', header: 'Last login', priority: false, render: (u) => <span className="text-muted-foreground">{fmt(u.last_login_at)}</span> },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (u) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => handleReset(u)} disabled={resetPassword.isPending}>
            Reset password
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleSetActive(u)} disabled={setActive.isPending}>
            {u.is_active ? 'Deactivate' : 'Reactivate'}
          </Button>
        </div>
      ),
    },
  ];

  const activityColumns: DataTableColumn<ActivityEntry>[] = [
    { key: 'when', header: 'When', priority: true, render: (e) => <span className="text-muted-foreground @2xl:text-foreground">{fmt(e.created_at)}</span> },
    { key: 'admin', header: 'Admin', priority: true, render: (e) => e.admin_email ?? '—' },
    { key: 'action', header: 'Action', priority: false, render: (e) => <span className="font-medium">{e.action}</span> },
    { key: 'resource', header: 'Resource', priority: false, render: (e) => <span className="text-muted-foreground">{[e.resource_type, e.resource_id].filter(Boolean).join(' · ') || '—'}</span> },
    { key: 'ip', header: 'IP', priority: false, render: (e) => <span className="font-mono text-xs text-muted-foreground">{e.ip_address ?? '—'}</span> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin Users"
        description="Staff with admin access and the audit trail. Roles are derived from Cognito groups; accounts self-provision on first login."
        actions={<Button onClick={() => setShowInvite(true)}>Invite admin</Button>}
      />

      <RoleAccessGuide />

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Staff</h2>
        {isLoading ? <Spinner /> : <DataTable columns={staffColumns} data={users} getRowKey={(u) => String(u.id)} emptyMessage="No admin users." />}
      </section>

      <section className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">Activity log</h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative sm:min-w-[15rem]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by action or resource…"
                className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <select
              value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
              aria-label="Filter by admin"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">All admins</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
            </select>
          </div>
        </div>
        {activityLoading ? (
          <Spinner />
        ) : (
          <>
            <DataTable columns={activityColumns} data={activity?.entries ?? []} getRowKey={(e) => String(e.id)} emptyMessage="No activity recorded." />
            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Page {pagination.page} of {pagination.pages} · {pagination.total.toLocaleString()} entries</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page <= 1}>Previous</Button>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))} disabled={pagination.page >= pagination.pages}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {showInvite && <InviteAdminDialog onClose={() => setShowInvite(false)} />}

      <Dialog.Root open={!!reset} onOpenChange={(open) => !open && setReset(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-card p-6 shadow-xl">
            <Dialog.Title className="text-base font-semibold">Password reset</Dialog.Title>
            <p className="mt-2 text-sm text-muted-foreground">
              Give this one-time temporary password to {reset?.email}. It is shown only now — they'll be forced to change it on next login.
            </p>
            <div className="mt-4 rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm">{reset?.password}</div>
            <div className="mt-6 flex justify-end">
              <Button size="sm" onClick={() => setReset(null)}>Done</Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
